import { Injectable } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { OfferFull, OfferStatus } from '@recruitee/http-types';
import { createEffectFactory } from '@recruitee/ngrx';

import { OfferScope, StatusChangeType } from './../../types';
import { TalentPoolsFullActions } from './talent-pools-full.store';
import { TalentPoolsHttpService } from './talent-pools-http.service';

@Injectable({
  providedIn: 'root',
})
export class TalentPoolsFullEffects {
  public effects = createEffectFactory(TalentPoolsFullActions, this.actions$);

  public fetchItem$ = this.effects(TalentPoolsFullActions.fetchOne, ({ payload }) =>
    this.talentPoolsHttpService
      .fetchItem$(payload)
      .pipe(map(resp => ({ id: payload.id, offer: resp.offer }))),
  );

  public create$ = this.effects(TalentPoolsFullActions.create, ({ payload }) =>
    this.talentPoolsHttpService.create$({ offer: payload.offer }).pipe(
      switchMap(response => [
        TalentPoolsFullActions.createSuccess({ payload: { data: response.offer } }),
      ]),
      catchError(error => [TalentPoolsFullActions.createFailed({ payload: error })]),
    ),
  );

  public update$ = this.effects(TalentPoolsFullActions.update, ({ payload }) =>
    this.talentPoolsHttpService.update$({ offer: payload.offer }).pipe(
      map(response => ({
        id: response.offer.id,
        data: response.offer,
      })),
      catchError(error => of({ id: payload.offer.id, error })),
    ),
  );

  public follow$ = this.effects(TalentPoolsFullActions.follow, ({ payload }) =>
    this.talentPoolsHttpService.follow(payload.id).pipe(
      switchMap(response => [
        TalentPoolsFullActions.followSuccess({ payload: { talentPool: response.offer } }),
      ]),
      catchError(error =>
        of(TalentPoolsFullActions.followFailed({ payload: { error, id: payload.id } })),
      ),
    ),
  );

  public unfollow$ = this.effects(TalentPoolsFullActions.unfollow, ({ payload }) =>
    this.talentPoolsHttpService.unfollow(payload.id).pipe(
      switchMap(response => [
        TalentPoolsFullActions.unfollowSuccess({ payload: { talentPool: response.offer } }),
      ]),
      catchError(error =>
        of(TalentPoolsFullActions.unfollowFailed({ payload: { error, id: payload.id } })),
      ),
    ),
  );

  public duplicate$ = this.effects(TalentPoolsFullActions.duplicate, ({ payload }) =>
    this.talentPoolsHttpService.duplicate$(payload.id).pipe(
      switchMap(response => [
        TalentPoolsFullActions.duplicateSuccess({ payload: { offer: response.offer } }),
      ]),
      catchError(error => [
        TalentPoolsFullActions.duplicateFailed({ payload: { id: payload.id, error } }),
      ]),
    ),
  );

  // eslint-disable-next-line max-lines-per-function
  public changeStatus$ = this.effects(TalentPoolsFullActions.changeStatus, ({ payload }) => {
    let status$: Observable<{ offer: OfferFull }>;
    const targetScope =
      payload.status === StatusChangeType.Archive ? OfferScope.Archived : OfferScope.Active;
    const isChangingScope =
      payload.currentStatus === OfferStatus.Archived || payload.status === StatusChangeType.Archive;

    switch (payload.status) {
      case StatusChangeType.Archive:
        status$ = this.talentPoolsHttpService.archive(payload.id);
        break;
      case StatusChangeType.Draft:
        status$ = this.talentPoolsHttpService.draft(payload.id);
        break;
      default:
        throw new Error(`Unknown status: ${payload.status}`);
    }

    return status$.pipe(
      switchMap(response => [
        TalentPoolsFullActions.changeStatusSuccess({
          payload: { offer: response.offer, isChangingScope, targetScope },
        }),
      ]),
      catchError(error => [
        TalentPoolsFullActions.changeStatusFailed({
          payload: { error, id: payload.id },
        }),
      ]),
    );
  });

  constructor(
    private actions$: Actions,
    private talentPoolsHttpService: TalentPoolsHttpService,
  ) {}
}
